/**
 * Shared presentational primitives for the game UI: KPI cards, sparklines,
 * deltas, meters. Pure SVG charts — no chart library needed, mobile-friendly.
 */

import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { money, num } from '@/game/format';

// ---------------------------------------------------------------------------
// Sparkline — lightweight SVG polyline w/ gradient fill
// ---------------------------------------------------------------------------

export function Sparkline({
  data,
  className,
  stroke = 'currentColor',
  height = 40,
  fill = true,
}: {
  data: number[];
  className?: string;
  stroke?: string;
  height?: number;
  fill?: boolean;
}) {
  if (data.length < 2) {
    return <div className={cn('h-10 text-muted-foreground/30', className)} />;
  }
  const w = 100;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = height - 4 - ((v - min) / range) * (height - 8);
    return [x, y] as const;
  });
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} ${w},${height} 0,${height}`;
  const gid = `spark-${stroke.replace(/[^a-z0-9]/gi, '')}`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className={cn('w-full', className)} aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.25" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill && <polygon points={area} fill={`url(#${gid})`} />}
      <polyline points={line} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Delta indicator
// ---------------------------------------------------------------------------

export function Delta({ value, invert = false }: { value: number; invert?: boolean }) {
  if (!Number.isFinite(value) || Math.abs(value) < 1e-9) {
    return <span className="text-xs text-muted-foreground">→ steady</span>;
  }
  const good = invert ? value < 0 : value > 0;
  return (
    <span className={cn('text-xs font-medium tabular-nums', good ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
      {value > 0 ? '▲' : '▼'} {invert ? money(Math.abs(value)) : `${Math.abs(value).toFixed(1)}%`}
    </span>
  );
}

// ---------------------------------------------------------------------------
// KPI card
// ---------------------------------------------------------------------------

export function KpiCard({
  label,
  value,
  sub,
  delta,
  deltaInvert,
  spark,
  sparkColor = 'hsl(var(--primary))',
  className,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  delta?: number;
  deltaInvert?: boolean;
  spark?: number[];
  sparkColor?: string;
  className?: string;
}) {
  return (
    <div className={cn('rounded-xl border bg-card p-4 shadow-sm', className)}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-xl font-semibold tabular-nums sm:text-2xl">{value}</span>
        {delta !== undefined && <Delta value={delta} invert={deltaInvert} />}
      </div>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      {spark && spark.length > 1 && (
        <div className="mt-2">
          <Sparkline data={spark} stroke={sparkColor} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Meter — labeled horizontal bar
// ---------------------------------------------------------------------------

export function Meter({
  label,
  value,
  max = 100,
  colorClass = 'bg-primary',
  valueText,
}: {
  label: string;
  value: number;
  max?: number;
  colorClass?: string;
  valueText?: string;
}) {
  const pctv = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{valueText ?? Math.round(value)}</span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full transition-all duration-500 motion-reduce:transition-none', colorClass)} style={{ width: `${pctv}%` }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section header within panels
// ---------------------------------------------------------------------------

export function PanelSection({ title, desc, children, right }: { title: string; desc?: string; children: ReactNode; right?: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-2">
        <div>
          <h3 className="font-semibold leading-tight">{title}</h3>
          {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

/** Compact unit count. */
export function Units({ n }: { n: number }) {
  return <span className="tabular-nums">{num(n)}</span>;
}
